class CartAdditionalFeatures extends HTMLElement {
  // CSS selector for things we consider focusable inside a panel —
  // used by the focus trap when this component runs in 'drawer' (modal) context.
  static FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  connectedCallback() {
    this.triggers = this.querySelectorAll(".wt-cart-features__trigger");
    this.drawers = this.querySelectorAll(".wt-cart-features__drawer");
    this.overlay = this.querySelector("[data-overlay]");
    this.closeButtons = this.querySelectorAll("[data-close]");
    this.context = this.dataset.context || "page";
    this.shopCurrency = this.dataset.shopCurrency || "";
    this.lastTrigger = null;

    // i18n strings — provided by the snippet via `data-i18n-*` attributes.
    // English fallbacks here mean the component still works if the
    // attributes are missing (e.g. older cached HTML). Convention:
    // hyphenated `data-i18n-foo-bar` becomes `dataset.i18nFooBar`.
    const ds = this.dataset;
    this.strings = {
      shippingSelect: ds.i18nShippingSelect || "Please select a country and enter a postal code.",
      shippingCalculating: ds.i18nShippingCalculating || "Calculating…",
      shippingNoRate: ds.i18nShippingNoRate || "There is no shipping rate for this destination. Try to check the address.",
      shippingOneRate: ds.i18nShippingOneRate || "There is one shipping rate for this destination:",
      // n-rates template uses `__COUNT__` as a placeholder; we substitute at use site.
      shippingNRatesTpl: ds.i18nShippingNRates || "There are __COUNT__ shipping rates for this destination:",
      shippingFree: ds.i18nShippingFree || "Free",
      discountEnter: ds.i18nDiscountEnter || "Please enter a discount code.",
      discountApplying: ds.i18nDiscountApplying || "Applying…",
      discountApplied: ds.i18nDiscountApplied || "Discount applied!",
      discountInvalid: ds.i18nDiscountInvalid || "Discount code is invalid or does not apply to the current cart.",
      discountError: ds.i18nDiscountError || "Something went wrong. Please try again.",
    };

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.closeAll = this.closeAll.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.triggers.forEach((t) => t.addEventListener("click", this.onTriggerClick));
    this.closeButtons.forEach((b) => b.addEventListener("click", this.closeAll));
    if (this.overlay) this.overlay.addEventListener("click", this.closeAll);
    this.addEventListener("keydown", this.onKeyDown);

    // a11y: collapsed panels must not be focusable. `max-height: 0;
    // overflow: hidden;` (desktop accordion) and `transform: translateY(100%)`
    // (mobile/cart-drawer slide-up) both hide visually but leave children
    // tab-reachable — `inert` removes them from the a11y tree + tab order.
    this.drawers.forEach((d) => {
      if (!d.hasAttribute("open")) d.setAttribute("inert", "");
    });

    this.initNote();
    this.initShipping();
    this.initDiscount();
  }

  disconnectedCallback() {
    this.discountEvents?.abort();
    this.discountUnsubscribe?.();
    this.discountUnsubscribe = null;
    this.removeEventListener("keydown", this.onKeyDown);
  }

  // ── Drawer toggling ─────────────────────────────────────────
  onTriggerClick(event) {
    const trigger = event.currentTarget;
    const feature = trigger.dataset.feature;
    const drawer = this.querySelector(`.wt-cart-features__drawer[data-drawer="${feature}"]`);

    // Toggle: clicking an already-open trigger collapses everything.
    // Used by the desktop accordion (cart-page ≥900px) so a second
    // click closes the panel; mobile slide-up behaves the same way.
    if (drawer && drawer.hasAttribute("open")) {
      this.closeAll();
      return;
    }

    this.openDrawer(feature);
  }

  openDrawer(feature) {
    this.closeAll();

    const drawer = this.querySelector(`.wt-cart-features__drawer[data-drawer="${feature}"]`);
    if (!drawer) return;

    const trigger = this.querySelector(`.wt-cart-features__trigger[data-feature="${feature}"]`);
    // Remember which trigger opened the panel so we can return focus
    // to it on close (a11y: don't drop focus to <body>).
    this.lastTrigger = trigger || null;

    drawer.setAttribute("open", "");
    drawer.removeAttribute("inert");
    if (trigger) {
      trigger.classList.add("wt-cart-features__trigger--open");
      trigger.setAttribute("aria-expanded", "true");
    }
    this.overlay?.setAttribute("open", "");
    this.classList.add("wt-cart-features--open");

    // Modal contexts (cart drawer slide-up): move focus into the panel
    // so it ends up inside the focus trap. The desktop cart-page
    // accordion keeps focus on the trigger so the trigger row reads
    // naturally as the heading.
    if (this.context === "drawer") {
      const firstField = drawer.querySelector(CartAdditionalFeatures.FOCUSABLE_SELECTOR);
      if (firstField) setTimeout(() => firstField.focus(), 50);
    }
  }

  closeAll() {
    const wasOpen = this.classList.contains("wt-cart-features--open");

    this.drawers.forEach((d) => {
      d.removeAttribute("open");
      // Re-disable interaction + tab order on every collapsed panel.
      d.setAttribute("inert", "");
    });
    this.triggers.forEach((t) => {
      t.classList.remove("wt-cart-features__trigger--open");
      t.setAttribute("aria-expanded", "false");
    });
    this.overlay?.removeAttribute("open");
    this.classList.remove("wt-cart-features--open");

    // Return focus to the trigger that opened the panel (a11y: keyboard
    // user shouldn't be dumped to <body>). Only do this if we were
    // genuinely closing something — avoid stealing focus on initial render.
    if (wasOpen && this.lastTrigger) {
      this.lastTrigger.focus();
    }
    this.lastTrigger = null;
  }

  onKeyDown(event) {
    if (!this.classList.contains("wt-cart-features--open")) return;

    if (event.key === "Escape") {
      this.closeAll();
      return;
    }

    // Focus trap — only meaningful in 'drawer' context where the panel is
    // a real modal (`role="dialog" aria-modal="true"`). On the cart page
    // the panel is an inline accordion / region, no trap needed.
    if (event.key === "Tab" && this.context === "drawer") {
      const openDrawer = this.querySelector(".wt-cart-features__drawer[open]");
      if (!openDrawer) return;

      const focusable = Array.from(
        openDrawer.querySelectorAll(CartAdditionalFeatures.FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !openDrawer.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  // ── Order note ──────────────────────────────────────────────
  initNote() {
    const textarea = this.querySelector("[data-note-textarea]");
    const saveBtn = this.querySelector("[data-note-save]");
    if (!textarea || !saveBtn) return;

    saveBtn.addEventListener("click", async () => {
      saveBtn.disabled = true;
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note: textarea.value }),
        });
        if (!response.ok) throw new Error("Failed to save note");
        this.closeAll();
      } catch (err) {
        console.error("[cart-features note]", err);
      } finally {
        saveBtn.disabled = false;
      }
    });
  }

  // ── Estimate Shipping ───────────────────────────────────────
  initShipping() {
    const submit = this.querySelector("[data-shipping-submit]");
    if (!submit) return;

    this.shippingSubmit = submit;
    this.shippingResult = this.querySelector("[data-shipping-result]");
    this.shippingCountry = this.querySelector("[data-shipping-country]");
    this.shippingZip = this.querySelector("[data-shipping-zip]");

    submit.addEventListener("click", this.onShippingSubmit.bind(this));
  }

  async onShippingSubmit() {
    const country = this.shippingCountry?.value || "";
    const zip = this.shippingZip?.value.trim() || "";

    if (!country || !zip) {
      this.renderShippingResult(this.strings.shippingSelect, "error");
      return;
    }

    this.shippingSubmit.disabled = true;
    this.renderShippingResult(this.strings.shippingCalculating, "loading");

    try {
      const url = `${window.Shopify.routes.root}cart/shipping_rates.json?shipping_address[country]=${encodeURIComponent(
        country,
      )}&shipping_address[zip]=${encodeURIComponent(zip)}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      const rates = data.shipping_rates || [];
      if (rates.length === 0) {
        this.renderShippingResult(this.strings.shippingNoRate, "error");
      } else {
        this.renderShippingRates(rates);
      }
    } catch (err) {
      console.error("[cart-features shipping]", err);
      this.renderShippingResult(this.strings.shippingNoRate, "error");
    } finally {
      this.shippingSubmit.disabled = false;
    }
  }

  renderShippingRates(rates) {
    const intro =
      rates.length === 1
        ? this.strings.shippingOneRate
        : this.strings.shippingNRatesTpl.replace("__COUNT__", String(rates.length));
    const items = rates
      .map((rate) => {
        const price = rate.price === "0.00" ? this.strings.shippingFree : this.formatMoney(rate.price);
        return `<li>${this.escape(rate.name)}: ${price}</li>`;
      })
      .join("");
    this.shippingResult.innerHTML = `<p>${intro}</p><ul class="wt-cart-features__rates">${items}</ul>`;
    this.shippingResult.dataset.state = "success";
  }

  renderShippingResult(message, state) {
    this.shippingResult.textContent = message;
    this.shippingResult.dataset.state = state;
  }

  formatMoney(stringAmount) {
    // /cart/shipping_rates.json returns `price` in the shop's settlement
    // (base) currency — e.g. "5.00" for a 5 PLN rate on a PLN shop. On a
    // multi-currency store the customer browses in a presentment currency
    // (`Shopify.currency.active`, e.g. "ALL"), and `Shopify.currency.rate`
    // holds the conversion factor from base → presentment.
    //
    // To match the rest of the cart UI (which shows everything in the
    // customer's presentment currency), convert the rate to presentment
    // and format with the active currency code.
    //   shop=PLN, customer=ALL, rate=21.5 → 5.00 PLN displayed as 107.50 L
    //
    // Falls through gracefully:
    //   - no presentment set → format the base amount with `shopCurrency`
    //   - missing or 1.0 rate → no conversion, format with whichever
    //     currency code is available
    const baseAmount = parseFloat(stringAmount);
    const presentment = window.Shopify?.currency?.active || "";
    const rate = parseFloat(window.Shopify?.currency?.rate || "1") || 1;
    const baseCode = this.shopCurrency || presentment || "USD";

    let amount = baseAmount;
    let code = baseCode;
    if (presentment && rate && rate !== 1) {
      amount = baseAmount * rate;
      code = presentment;
    } else if (presentment) {
      // Same currency on both sides (or rate not provided) — use
      // presentment so the code matches the rest of the storefront.
      code = presentment;
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2)} ${code}`;
    }
  }

  escape(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  // PRISON SEMFRETE 2026-09-27: validate exact codes, not changes to item totals.
  static discountBusy = false;
  static discountRevision = 0;
  static discountRead = null;

  static normalizeCode(code) {
    return String(code || "").trim().toUpperCase();
  }

  static discountEntries(cart) {
    if (Array.isArray(cart?.discount_codes)) {
      const seen = new Set();
      return cart.discount_codes.filter((entry) => {
        const key = this.normalizeCode(entry?.code);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    // Compatibility only when the exact-code API field is absent.
    const applications = [
      ...(cart?.cart_level_discount_applications || []),
      ...(cart?.discount_applications || []),
      ...(cart?.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)),
    ];
    const seen = new Set();
    return applications.filter((app) => {
      const key = this.normalizeCode(app?.title);
      if (app?.type !== "discount_code" || !key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((app) => ({ code: app.title, applicable: true }));
  }

  static async discountRequest(path, body) {
    const root = window.Shopify?.routes?.root || "/";
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${root}${path}`, {
        method: body === undefined ? "GET" : "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: controller.signal,
      });
      const cart = await response.json();
      if (!response.ok || cart?.errors || !Array.isArray(cart?.items)) {
        throw new Error(`Cart request failed (${response.status})`);
      }
      return cart;
    } finally {
      clearTimeout(timeout);
    }
  }

  initDiscount() {
    this.discountEvents?.abort();
    this.discountUnsubscribe?.();
    this.discountEvents = new AbortController();
    const options = { signal: this.discountEvents.signal };
    this.discountInput = this.querySelector("[data-discount-input]");
    this.discountResult = this.querySelector("[data-discount-result]");
    this.applyBtn = this.querySelector("[data-discount-apply]");
    if (!this.applyBtn) return;

    this.addEventListener("click", (event) => {
      const button = event.target.closest?.("[data-discount-apply], [data-discount-remove-code]");
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (button.hasAttribute("data-discount-apply")) this.onDiscountApply();
      else this.onDiscountRemoveCode(button.dataset.discountRemoveCode);
    }, options);
    this.discountInput?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      event.stopPropagation();
      this.onDiscountApply();
    }, options);
    this.discountInput?.addEventListener("input", () => {
      delete this.discountInput.dataset.error;
      this.discountInput.removeAttribute("aria-invalid");
      this.renderDiscountResult("", "");
    }, options);
    this.querySelector('[data-feature="discount"]')?.addEventListener("click", () => this.hydrateDiscountCodes(), options);
    const update = () => this.hydrateDiscountCodes();
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.discountUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, update);
    }
    document.addEventListener("cart-drawer:refresh", update, options);
    window.addEventListener("pageshow", update, options);
    this.hydrateDiscountCodes();
    this.setDiscountBusy(CartAdditionalFeatures.discountBusy);
  }

  setDiscountBusy(busy) {
    this.querySelectorAll("[data-discount-apply], [data-discount-remove-code]").forEach((button) => {
      button.disabled = busy;
      button.setAttribute("aria-busy", String(busy));
    });
  }

  async hydrateDiscountCodes() {
    const Klass = CartAdditionalFeatures;
    if (!this.applyBtn || !this.isConnected || Klass.discountBusy) return;
    const revision = Klass.discountRevision;
    try {
      if (!Klass.discountRead) {
        const request = Klass.discountRequest("cart.js");
        Klass.discountRead = request;
        request.finally(() => {
          if (Klass.discountRead === request) Klass.discountRead = null;
        }).catch(() => {});
      }
      const cart = await Klass.discountRead;
      if (this.isConnected && revision === Klass.discountRevision && !Klass.discountBusy) {
        this.renderDiscountCodes(cart);
      }
    } catch (error) {
      if (revision === Klass.discountRevision) this.querySelector("[data-ps-discount-status]")?.remove();
    }
  }

  renderDiscountCodes(cart) {
    const Klass = CartAdditionalFeatures;
    const entries = cart.item_count === 0 ? [] : Klass.discountEntries(cart);
    this.dataset.discountCodes = entries.map((entry) => entry.code).join(",");
    // Liquid allocations omit shipping codes; use the authoritative cart list.
    this.querySelectorAll(".wt-cart-features__discount-list").forEach((list) => {
      list.hidden = true;
      list.style.display = "none";
    });
    this.querySelector("[data-ps-discount-status]")?.remove();
    if (!entries.length) return;
    const status = document.createElement("div");
    status.dataset.psDiscountStatus = "";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.style.cssText = "display:grid;gap:6px;margin:8px 0;min-width:0;";
    for (const entry of entries) {
      const code = Klass.normalizeCode(entry.code);
      const accepted = entry.applicable === true;
      const row = document.createElement("div");
      row.dataset.psCode = code;
      row.dataset.state = accepted ? "success" : "pending";
      row.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:10px;padding:9px 11px;border:1px solid #ddd;font-size:12px;line-height:1.45;min-width:0;";
      row.style.background = accepted ? "#eef7f0" : "#f6f6f6";
      row.style.color = accepted ? "#176b38" : "#555";
      const label = document.createElement("span");
      label.style.cssText = "min-width:0;overflow-wrap:anywhere;";
      const title = document.createElement("strong");
      title.textContent = accepted ? `${code} adicionado` : `${code} — não aplicado neste carrinho`;
      const note = document.createElement("span");
      note.style.cssText = "display:block;font-size:11px;";
      note.textContent = code === "SEMFRETE"
        ? (accepted ? "Frete grátis sujeito à validação do endereço e das condições no checkout." : "Confira o endereço e as condições do cupom no checkout.")
        : (accepted ? "Desconto considerado pela Shopify nesta sacola." : "Confira as condições e a combinação com outros descontos.");
      label.append(title, note);
      const remove = document.createElement("button");
      remove.type = "button";
      remove.dataset.discountRemoveCode = entry.code;
      remove.textContent = "Remover";
      remove.setAttribute("aria-label", `Remover cupom ${code}`);
      remove.style.cssText = "border:0;background:transparent;color:inherit;text-decoration:underline;font-size:11px;padding:6px;cursor:pointer;flex-shrink:0;";
      remove.disabled = Klass.discountBusy;
      row.append(label, remove);
      status.appendChild(row);
    }
    // This remains visible when the coupon panel is closed.
    const triggers = this.querySelector(".wt-cart-features__triggers");
    if (triggers) triggers.after(status);
    else this.appendChild(status);
  }

  isDiscountApplied(cartAfter, code) {
    const Klass = CartAdditionalFeatures;
    return Klass.discountEntries(cartAfter).some((entry) =>
      Klass.normalizeCode(entry.code) === Klass.normalizeCode(code) && entry.applicable === true);
  }

  async changeDiscountCode(code, remove = false) {
    const Klass = CartAdditionalFeatures;
    if (Klass.discountBusy) return;
    Klass.discountBusy = true;
    Klass.discountRevision += 1;
    Klass.discountRead = null;
    const components = () => document.querySelectorAll("cart-additional-features");
    components().forEach((component) => component.setDiscountBusy?.(true));
    this.renderDiscountResult(remove ? "Removendo cupom…" : this.strings.discountApplying, "loading");
    try {
      const before = await Klass.discountRequest("cart.js");
      const normalCode = Klass.normalizeCode(code);
      const codes = Klass.discountEntries(before).map((entry) => entry.code)
        .filter((existing) => Klass.normalizeCode(existing) !== normalCode);
      if (!remove) codes.push(code.trim());
      const updated = await Klass.discountRequest("cart/update.js", { discount: codes.join(",") });
      components().forEach((component) => component.renderDiscountCodes?.(updated));
      const applied = !remove && this.isDiscountApplied(updated, code);
      if (remove) {
        if (Klass.discountEntries(updated).some((entry) => Klass.normalizeCode(entry.code) === normalCode)) {
          throw new Error("Coupon removal was not confirmed");
        }
        this.renderDiscountResult("Cupom removido.", "success");
      } else if (applied) {
        this.renderDiscountResult(normalCode === "SEMFRETE"
          ? "SEMFRETE adicionado. O frete será validado no checkout."
          : this.strings.discountApplied, "success");
        this.discountInput?.removeAttribute("aria-invalid");
      } else {
        this.renderDiscountResult(normalCode === "SEMFRETE"
          ? "SEMFRETE não foi aplicado neste carrinho. Confira o endereço e as condições no checkout."
          : this.strings.discountInvalid, "error");
        if (this.discountInput) {
          this.discountInput.dataset.error = "";
          this.discountInput.setAttribute("aria-invalid", "true");
        }
      }
      if (applied || remove) {
        this.closeAll();
        this.refreshCart();
      } else if (updated.total_price !== before.total_price || updated.total_discount !== before.total_discount) {
        this.refreshCart();
      }
    } catch (error) {
      this.renderDiscountResult("Não foi possível confirmar o cupom agora. Tente novamente ou confira no checkout.", "error");
      components().forEach((component) => component.querySelector("[data-ps-discount-status]")?.remove());
      console.error("[cart-features discount]", error);
    } finally {
      Klass.discountBusy = false;
      Klass.discountRevision += 1;
      components().forEach((component) => component.setDiscountBusy?.(false));
    }
  }

  onDiscountApply() {
    const code = (this.discountInput?.value || "").trim();
    if (!code) {
      this.renderDiscountResult(this.strings.discountEnter, "error");
      return;
    }
    if (code.includes(",")) {
      this.renderDiscountResult("Digite apenas um cupom por vez.", "error");
      return;
    }
    return this.changeDiscountCode(code);
  }

  onDiscountRemoveCode(codeToRemove) {
    return this.changeDiscountCode(codeToRemove, true);
  }

  renderDiscountResult(message, state) {
    if (!this.discountResult) return;
    this.discountResult.textContent = message;
    if (state) this.discountResult.dataset.state = state;
    else delete this.discountResult.dataset.state;
  }

  async refreshCart() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
      cartDrawer.refreshCartDrawer();
    }

    // 2. Cart page — fetch fresh `main-cart-items` + `main-cart-footer`
    //    + `cart-icon-bubble` sections and swap their `.js-contents` /
    //    `.shopify-section` regions. Without this the cart page would
    //    stay stale (subtotal, savings, line discounts) after applying
    //    or removing a discount code on the page.
    const cartItemsEl = document.getElementById("main-cart-items");
    const cartFooterEl = document.getElementById("main-cart-footer");

    if (cartItemsEl || cartFooterEl) {
      const sectionIds = [];
      if (cartItemsEl?.dataset.id) sectionIds.push(cartItemsEl.dataset.id);
      if (cartFooterEl?.dataset.id) sectionIds.push(cartFooterEl.dataset.id);
      sectionIds.push("cart-icon-bubble");

      try {
        const response = await fetch(
          `${window.Shopify.routes.root}?sections=${sectionIds.join(",")}`,
        );
        const data = await response.json();

        const targets = [
          { key: cartItemsEl?.dataset.id, el: cartItemsEl, selector: ".js-contents" },
          { key: cartFooterEl?.dataset.id, el: cartFooterEl, selector: ".js-contents" },
          {
            key: "cart-icon-bubble",
            el: document.getElementById("shopify-section-cart-icon-bubble"),
            selector: ".shopify-section",
          },
        ];

        targets.forEach((target) => {
          if (!target.key || !target.el || !data[target.key]) return;
          const parsed = new DOMParser().parseFromString(data[target.key], "text/html");
          const incoming = parsed.querySelector(target.selector);
          const existing = target.el.querySelector(target.selector);
          if (incoming && existing) {
            existing.innerHTML = incoming.innerHTML;
          }
        });
      } catch (err) {
        console.error("[cart-features] section refresh", err);
      }
    }

    // 3. Notify pub/sub subscribers (cart icon bubble, sticky bars, …).
    //    Convention from CLAUDE.md: "JS — Pub/Sub events via constants.js
    //    (PUB_SUB_EVENTS)". Same call as cart-service-checkbox.js.
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-additional-features" });
    }
  }
}

if (!customElements.get("cart-additional-features")) {
  customElements.define("cart-additional-features", CartAdditionalFeatures);
}
